import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableHead,
  TableContainer,
  Typography,
  Box,
  Chip,
  Checkbox,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";

function InvoicePreviewDialog({ open, onClose, previewData, previewLoading, previewError, onConfirm, confirmLoading, className }) {
  const [selectedIds, setSelectedIds] = useState([]);

  // Default to every previewed student selected whenever a fresh preview loads.
  useEffect(() => {
    if (previewData?.students) {
      setSelectedIds(previewData.students.map((s) => s.id));
    }
  }, [previewData]);

  const toggleStudent = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const allSelected =
    (previewData?.students?.length ?? 0) > 0 &&
    previewData.students.every((s) => selectedIds.includes(s.id));

  const toggleAll = (checked) => {
    setSelectedIds(checked ? (previewData?.students || []).map((s) => s.id) : []);
  };

  const selectedStudents = (previewData?.students || []).filter((s) =>
    selectedIds.includes(s.id)
  );
  const selectedTotal = selectedStudents.reduce((sum, s) => sum + (s.amount || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6">Invoice Preview</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseSharpIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent>
        {previewLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={36} />
          </Box>
        )}

        {previewError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load preview. Please try again.
          </Alert>
        )}

        {previewData && !previewLoading && (
          <>
            {/* Summary cards */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                flexWrap: "wrap",
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              {className && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    CLASS
                  </Typography>
                  <Typography fontWeight="bold">{className}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  SELECTED STUDENTS
                </Typography>
                <Typography fontWeight="bold">
                  {selectedStudents.length} / {previewData.student_count}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  GRAND TOTAL (selected)
                </Typography>
                <Typography fontWeight="bold" color="primary.main">
                  ₦{selectedTotal.toLocaleString()}
                </Typography>
              </Box>
            </Box>

            {/* Fee items */}
            <Typography variant="formSubHeading" sx={{ mb: 1 }}>
              FEE ITEMS
            </Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ borderBottom: "2px solid lightgrey" }}>
                    <StyledTableCell>NAME</StyledTableCell>
                    <StyledTableCell>AMOUNT</StyledTableCell>
                    <StyledTableCell>TYPE</StyledTableCell>
                    <StyledTableCell>GENDER</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.fees?.length > 0 ? (
                    previewData.fees.map((fee) => (
                      <StyledTableRow key={fee.id}>
                        <StyledTableCell>{fee.name}</StyledTableCell>
                        <StyledTableCell>₦{(fee.amount || 0).toLocaleString()}</StyledTableCell>
                        <StyledTableCell>
                          <Chip
                            label={fee.is_compulsory ? "Compulsory" : "Optional"}
                            color={fee.is_compulsory ? "default" : "info"}
                            size="small"
                            variant="outlined"
                          />
                        </StyledTableCell>
                        <StyledTableCell>{fee.gender || "All"}</StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <StyledTableRow>
                      <StyledTableCell colSpan={4}>No fee items selected</StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Students */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="formSubHeading">
                STUDENTS ({previewData.student_count})
              </Typography>
              {previewData.student_count > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Select all</Typography>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selectedIds.length > 0 && !allSelected}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </Box>
              )}
            </Box>
            {previewData.student_count === 0 ? (
              <Alert severity="warning">No students found.</Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "2px solid lightgrey" }}>
                      <StyledTableCell padding="checkbox"></StyledTableCell>
                      <StyledTableCell>NAME</StyledTableCell>
                      <StyledTableCell>AMOUNT</StyledTableCell>
                      <StyledTableCell>STATUS</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.students?.map((student) => (
                      <StyledTableRow key={student.id}>
                        <StyledTableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedIds.includes(student.id)}
                            onChange={() => toggleStudent(student.id)}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {`${student.first_name || ""} ${student.last_name || ""}`.trim() || "—"}
                        </StyledTableCell>
                        <StyledTableCell>₦{(student.amount || 0).toLocaleString()}</StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {student.has_existing_invoice && (
                              <Chip label="Already invoiced" color="warning" size="small" variant="outlined" />
                            )}
                            {!student.gender && (
                              <Chip label="No gender set" color="error" size="small" variant="outlined" />
                            )}
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <StyledLoadingButton
          variant="outlined"
          sx={{ textTransform: "none" }}
          onClick={onClose}
          disabled={confirmLoading}
        >
          Cancel
        </StyledLoadingButton>
        <StyledLoadingButton
          loading={confirmLoading}
          disabled={!previewData || selectedIds.length === 0 || previewLoading}
          sx={{ textTransform: "none" }}
          onClick={() => onConfirm(selectedIds)}
        >
          Confirm &amp; Generate ({selectedIds.length})
        </StyledLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default InvoicePreviewDialog;
